import React, { useState, useEffect, useMemo, useCallback } from 'react';
import colors from '@albertsons/uds/foundation/color';
import FileUpload from './FileUpload';
import { authProvider } from '../utils/authProvider';
import { useUserPermissions } from '../context/UserPermissionsContext';
import './DCFileUpload.css';
import { useNavigate } from 'react-router';
import { getMandatoryFieldKeys } from './tabConfig'; // Import the new config
import { Tooltip, CircularProgress } from '@mui/material';
import { broadcastDCChange } from '../utils/dcEvents';
import { normalizeCapabilityKey } from '../services/settingsServices/userRolesService';

// Define a simple component for the tooltip content to keep the main code clean
const TooltipContent: React.FC<{ fields: string[]; note?: string }> = ({ fields, note }) => (
    <>
        <b>Mandatory Fields:</b>
        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
            {fields.map(field => <li key={field}>{field}</li>)}
        </ul>
        {note && <em>{note}</em>}
    </>
);

interface DCFileUploadProps {
    activeTab?: number;
}

const DCFileUpload: React.FC<DCFileUploadProps> = ({ activeTab }) => {
    const [tabIndex, setTabIndex] = useState(activeTab ?? 0);
    const [selectedDC, setSelectedDC] = useState("");
    const auth = authProvider();
    const { userPermissions, loading: permissionsLoading } = useUserPermissions();
    const navigate = useNavigate();
    const storedDC = sessionStorage.getItem('selectedDC') || "";

    useEffect(() => {
        if (activeTab !== undefined) {
            setTabIndex(activeTab);
        }
    }, [activeTab]);

    const filteredGroups = useMemo(() => {
        if (!userPermissions) return [];
        const dcSet = new Set<string>();
        (userPermissions.roles || []).forEach(role => {
            if (role.dcCode) dcSet.add(role.dcCode);
        });
        return Array.from(dcSet).sort();
    }, [userPermissions]);

    const hasCapability = useCallback((capability: string): boolean => {
        if (!userPermissions || !selectedDC) return false;
        const target = normalizeCapabilityKey(capability);
        return userPermissions.roles
            .filter(role => role.dcCode === selectedDC)
            .some(role =>
                (role.capabilities || []).some(cap => normalizeCapabilityKey(cap) === target)
            );
    }, [userPermissions, selectedDC]);

    useEffect(() => {
        if (filteredGroups.length === 0) return;
        if (!selectedDC) {
            // Use storedDC only if it's still valid; otherwise pick the first DC
            const validStored = storedDC && filteredGroups.includes(storedDC) ? storedDC : filteredGroups[0];
            setSelectedDC(validStored);
            sessionStorage.setItem('selectedDC', validStored);
            broadcastDCChange(validStored);
        } else if (!filteredGroups.includes(selectedDC)) {
            // Current selection is no longer in the allowed list — reset
            setSelectedDC(filteredGroups[0]);
            sessionStorage.setItem('selectedDC', filteredGroups[0]);
            broadcastDCChange(filteredGroups[0]);
        } else {
            sessionStorage.setItem('selectedDC', selectedDC);
            broadcastDCChange(selectedDC);
        }
    }, [filteredGroups, selectedDC, storedDC]);

    // Auto-switch to first available tab when DC changes or user doesn't have access to current tab
    useEffect(() => {
        if (selectedDC) {
            const tabAccessRules = [
                { tabIndex: 0, operationType: 'xdock', requiresAuth: true },
                { tabIndex: 1, operationType: 'in02', requiresAuth: true }
            ];

            const currentTabRule = tabAccessRules.find(rule => rule.tabIndex === tabIndex);
            const currentTabAccessible = currentTabRule &&
                (!currentTabRule.requiresAuth || hasCapability(currentTabRule.operationType));

            if (!currentTabAccessible) {
                const firstAvailableTab = tabAccessRules.find(rule =>
                    !rule.requiresAuth || hasCapability(rule.operationType)
                );

                if (firstAvailableTab) {
                    setTabIndex(firstAvailableTab.tabIndex);
                    const routes = [
                        '/wms-upload-ui/dc-file-upload/xdock-upload',
                        '/wms-upload-ui/dc-file-upload/in02-upload'
                    ];
                    navigate(routes[firstAvailableTab.tabIndex]);
                }
            }
        }
    }, [selectedDC, userPermissions, tabIndex, navigate, hasCapability]);

    return (
        <div>
            <div className='h-[10px] w-full px-7' style={{ backgroundColor: colors['gray-208'] }}>
                <div className='flex items-center mt-1'>
                    <span className='md:text-[28px] text-[24px] whitespace-nowrap font-bold '>
                        WMS Operations Hub
                    </span>
                    <div className='dropDownContainer'>
                        <select
                            value={selectedDC}
                            onChange={(e) => setSelectedDC(e.target.value)}
                            disabled={!userPermissions}
                            className={`customSelect ${!userPermissions ? 'disabled' : 'enabled'}`}
                        >
                            <option value="">Select DC</option>
                            {filteredGroups.map((group: string) => (
                                <option key={group} value={group}>{group}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
            {/* Tabs Header */}
            <div className="tabs">
                {permissionsLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: '8px' }}>
                        <CircularProgress size={18} />
                        <span style={{ fontSize: '14px', color: '#64748b' }}>Loading permissions...</span>
                    </div>
                ) : ((() => {
                    const tabConfigs = [
                        {
                            id: 0,
                            operationType: 'xdock',
                            label: 'XDOCK Upload',
                            route: '/wms-upload-ui/dc-file-upload/xdock-upload',
                            requiresAuth: true,
                            tooltip: <TooltipContent 
                                fields={getMandatoryFieldKeys('XDOCK', selectedDC)}
                                note="File must be in .csv format."
                            />
                        },
                        {
                            id: 1,
                            operationType: 'in02',
                            label: 'Shipment Planning Upload (IN02)',
                            route: '/wms-upload-ui/dc-file-upload/in02-upload',
                            requiresAuth: true,
                            tooltip: <TooltipContent 
                                fields={getMandatoryFieldKeys('IN02', selectedDC)}
                                note="File must be in .csv format."
                            />
                        }
                        
                    ];
                    
                    return tabConfigs.map(tab => {
                        const shouldShow = !tab.requiresAuth || hasCapability(tab.operationType);
                        
                        if (!shouldShow) return null;
                        
                        return (
                            <div
                                key={tab.id}
                                className={`tab ${tabIndex === tab.id ? 'active' : ''}`}
                                onClick={() => {
                                    setTabIndex(tab.id);
                                    navigate(tab.route);
                                }}
                            >
                                {tab.label}
                                {tab.tooltip && (
                                    <Tooltip title={tab.tooltip} arrow placement="bottom">
                                        <span style={{ cursor: 'help', color: '#0073e6', fontSize: '16px', marginLeft: '4px' }}>ℹ</span>
                                    </Tooltip>
                                )}
                            </div>
                        );
                    }).filter(Boolean);
                })())}
            </div>

            {/* Tabs Content */}
            <div className="tab-content">
                {(() => {
                    if (!selectedDC) {
                        return <div className='under-construction'>Please select a DC to continue.</div>;
                    }
                    
                    // Define content configurations for future extensibility
                    const contentConfigs = [
                        {
                            tabIndex: 0,
                            operationType: 'xdock',
                            requiresAuth: true,
                            component: () => (
                                <FileUpload 
                                    selectedDC={selectedDC}
                                    tabType="XDOCK"
                                    onUpload={(files: File[]) => console.log(files)} 
                                    onUploadFilTable={(filesFillTable: File[]) => console.log(filesFillTable)}
                                    userId={auth.userId}
                                />
                            )
                        },
                        {
                            tabIndex: 1,
                            operationType: 'in02',
                            requiresAuth: true,
                            component: () => (
                                <FileUpload 
                                    selectedDC={selectedDC}
                                    tabType="IN02"
                                    onUpload={(files: File[]) => console.log(files)} 
                                    onUploadFilTable={(filesFillTable: File[]) => console.log(filesFillTable)}
                                    userId={auth.userId}
                                />
                            )
                        }
                        
                    ];
                    
                    // Find the configuration for current tab
                    const currentConfig = contentConfigs.find(config => config.tabIndex === tabIndex);
                    
                    if (!currentConfig) {
                        return <div className='under-construction'>Invalid tab selection.</div>;
                    }
                    
                    // Check if user has access to this tab content
                    const hasAccess = !currentConfig.requiresAuth || hasCapability(currentConfig.operationType);
                    
                    if (!hasAccess) {
                        return <div className='under-construction'>You don't have access to this operation for the selected DC.</div>;
                    }
                    
                    // Render the component
                    return currentConfig.component();
                })()}
            </div>
        </div>
    );
};

export default DCFileUpload;